import {
  createContext,
  useContext,
  useCallback,
  useState,
  ReactNode
} from 'react';

import { MainBlogPost, IAuthor, IBanner } from '~types/main';
import { safeJsonParse } from '@/src/utils/storage';

type BlogContextData = {
  blogPost: MainBlogPost;
  setBlogPost: (blogPost: MainBlogPost) => void;
  updateBlogPost: (blogPost: Partial<MainBlogPost>) => void;
  author: IAuthor;
  setAuthor: (author: IAuthor) => void;
  updateAuthor: (author: Partial<IAuthor>) => void;
  banner: IBanner;
  setBanner: (banner: IBanner) => void;
}

const BlogContext = createContext<BlogContextData>({} as BlogContextData);

type ProviderProps = {
  children: ReactNode;
}

export function BlogProvider({ children }: ProviderProps) {
  const [currentBlogPost, setCurrentBlogPost] = useState<MainBlogPost>(() => {
    const blogPost = localStorage.getItem('@auge:post');
    return safeJsonParse<MainBlogPost>(blogPost, {} as MainBlogPost);
  });

  const [currentAuthor, setCurrentAuthor] = useState<IAuthor>(() => {
    const author = localStorage.getItem('@auge:author');
    return safeJsonParse<IAuthor>(author, {} as IAuthor);
  });

  const [currentBanner, setCurrentBanner] = useState<IBanner>(() => {
    const banner = localStorage.getItem('@auge:banner');
    return safeJsonParse<IBanner>(banner, {} as IBanner);
  });

  const updateBlogPost = useCallback((blogPost: Partial<MainBlogPost>) => {
    setCurrentBlogPost(prev => {
      const updatedBlogPost = { ...prev, ...blogPost };
      localStorage.setItem('@auge:post', JSON.stringify(updatedBlogPost));
      return updatedBlogPost;
    });
  }, []);

  const handleSetBlogPost = useCallback((updatedBlogPost: MainBlogPost) => {
    localStorage.setItem('@auge:post', JSON.stringify(updatedBlogPost));
    setCurrentBlogPost(updatedBlogPost);
  }, []);

  const updateAuthor = useCallback((author: Partial<IAuthor>) => {
    setCurrentAuthor(prev => {
      const updatedAuthor = { ...prev, ...author };
      localStorage.setItem('@auge:author', JSON.stringify(updatedAuthor));
      return updatedAuthor;
    });
  }, []);

  const handleSetAuthor = useCallback((updatedAuthor: IAuthor) => {
    localStorage.setItem('@auge:author', JSON.stringify(updatedAuthor));
    setCurrentAuthor(updatedAuthor);
  }, []);

  const handleSetBanner = useCallback((updatedBanner: IBanner) => {
    localStorage.setItem('@auge:banner', JSON.stringify(updatedBanner));
    setCurrentBanner(updatedBanner);
  }, []);

  return (
    <BlogContext.Provider
      value={{
        blogPost: currentBlogPost,
        setBlogPost: handleSetBlogPost,
        updateBlogPost,
        author: currentAuthor,
        setAuthor: handleSetAuthor,
        updateAuthor,
        banner: currentBanner,
        setBanner: handleSetBanner,
      }}
    >
      {children}
    </BlogContext.Provider>
  );
}

export function useBlog() {
  const context = useContext(BlogContext);

  if (!context) {
    throw new Error("useBlog must be used within a BlogProvider.");
  }

  return context;
}
